/**
 * DesignerAssistant – keyword-based AI assistant for the Selenny Designer.
 * Completely offline, no external API needed. Uses a built-in German knowledge
 * base with fuzzy keyword matching and scoring.
 */

// Normalise a string: lowercase, replace German umlauts, collapse whitespace
function normalize(text) {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// ────────────────────────────────────────────────────
// Knowledge Base
// ────────────────────────────────────────────────────

const KNOWLEDGE_BASE = [
  // ── Text ──
  {
    id: 'add-text',
    keywords: ['text einfuegen', 'text hinzufuegen', 'text schreiben', 'schrift', 'schriftart', 'font', 'textfeld', 'ueberschrift', 'buchstabe', 'text'],
    answer:
      'Um Text einzufügen, klicke auf das **T-Symbol** in der linken Werkzeugleiste. Es öffnet sich ein Dialog mit Textvorlagen, eigenem Text und Effekten. Du kannst Schriftart, Größe und Farbe direkt einstellen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-text"]', label: 'Text einfügen' },
      { type: 'open', panel: 'text' },
    ],
  },

  // ── Shapes ──
  {
    id: 'add-shapes',
    keywords: ['form', 'formen', 'shape', 'rechteck', 'kreis', 'dreieck', 'stern', 'herz', 'pfeil', 'ellipse', 'polygon', 'geometrie', 'figur'],
    answer:
      'Klicke auf das **Formen-Symbol** (oben links in der Werkzeugleiste). Es erscheint eine Auswahl mit Rechtecken, Kreisen, Dreiecken, Sternen, Herzen, Pfeilen und vielen weiteren Formen. Einfach auf die gewünschte Form klicken – sie wird sofort auf der Leinwand platziert.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-shapes"]', label: 'Formen einfügen' },
      { type: 'open', panel: 'shapes' },
    ],
  },

  // ── Image ──
  {
    id: 'upload-image',
    keywords: ['bild', 'bild einfuegen', 'bild hinzufuegen', 'foto', 'foto einfuegen', 'image', 'hochladen', 'upload', 'datei', 'jpg', 'png', 'grafik', 'logo', 'einfuegen bild'],
    answer:
      'Klicke auf das **Bild-Symbol** in der linken Werkzeugleiste (Landschaftsbild-Icon). Dann kannst du eine Bilddatei (JPG, PNG, WebP, SVG) von deinem Computer hochladen. Das Bild wird direkt auf der Leinwand platziert und kann verschoben und skaliert werden.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-image"]', label: 'Bild hochladen' },
    ],
  },

  // ── Collage ──
  {
    id: 'collage',
    keywords: ['collage', 'fotocollage', 'layout', 'layouts', 'foto collage', 'bildcollage', 'raster', 'grid collage', 'fotos anordnen', 'bildergalerie', 'foto raster'],
    answer:
      'Klicke auf **„Collagen Layouts"** in der linken Werkzeugleiste. Wähle ein vorgefertigtes Layout (1–12+ Fotos), dann klicke „Layout anwenden". Es erscheint das Foto-Zuordnungsfenster, wo du Bilder per Klick oder Drag & Drop auf die Platzhalter ziehen kannst.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-collage"]', label: 'Collagen Layouts' },
      { type: 'open', panel: 'collage' },
    ],
  },

  // ── Zahlencollage ──
  {
    id: 'number-collage',
    keywords: ['zahlencollage', 'geburtstag', 'zahl', 'zahlen collage', 'birthday', 'jubilaeumszahl', 'alterszahl', '50 geburtstag', '30 geburtstag', '18 geburtstag', 'spruch', 'geburtstagsspruch'],
    answer:
      'Klicke auf **„Collagen Layouts"** → Tab **„Zahlencollage"**. Wähle eine Zahl (z.B. 50 für den 50. Geburtstag) und optional einen Spruch. Die Fotos werden in der Form der Zahl angeordnet. Nach dem Erstellen kannst du Fotos per Klick auf die Platzhalter zuordnen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-collage"]', label: 'Collagen Layouts → Zahlencollage' },
      { type: 'open', panel: 'collage' },
    ],
  },

  // ── QR Code ──
  {
    id: 'qr-code',
    keywords: ['qr', 'qr-code', 'qrcode', 'barcode', 'code', 'url scannen', 'link code'],
    answer:
      'Klicke auf das **QR-Code-Symbol** in der linken Werkzeugleiste. Gib dann die gewünschte URL oder einen Text ein und klicke auf "QR-Code einfügen". Der QR-Code wird als Bild auf der Leinwand platziert.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-qr"]', label: 'QR-Code einfügen' },
      { type: 'open', panel: 'qr' },
    ],
  },

  // ── Background color ──
  {
    id: 'bgcolor',
    keywords: ['hintergrund', 'hintergrundfarbe', 'background', 'farbe hintergrund', 'canvas farbe', 'leinwandfarbe', 'bgcolor', 'weiss', 'weisser hintergrund'],
    answer:
      'Klicke auf **„Hintergrundfarbe"** in der linken Werkzeugleiste. Dort kannst du aus vordefinierten Farben wählen oder über **„Eigene Farbe mischen"** eine beliebige Farbe auswählen. Mit „Zurücksetzen (Weiß)" setzt du die Farbe auf Weiß zurück. Tipp: Beim Entfernen eines Hintergrundbildes wird die Farbe automatisch auf Weiß zurückgesetzt.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-bgcolor"]', label: 'Hintergrundfarbe' },
      { type: 'open', panel: 'bgcolor' },
    ],
  },

  // ── Dimensions ──
  {
    id: 'dimensions',
    keywords: ['groesse', 'masse', 'abmessung', 'dimension', 'breite', 'hoehe', 'format', 'cm', 'din', 'a4', 'a3', 'a2', 'a1', 'a0', 'motivgroesse'],
    answer:
      'Die Motivgröße kannst du im rechten **Produkt-Panel** ändern. Gib Breite und Höhe in Zentimetern ein, oder wähle eine Standardgröße (DIN A4 bis DIN A0) aus dem Dropdown-Menü.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="panel-dimensions"]', label: 'Motivgröße (cm)' },
    ],
  },

  // ── Bleed / Beschnitt ──
  {
    id: 'bleed',
    keywords: ['beschnitt', 'anschnitt', 'bleed', 'randanschnitt', 'schnittrand', 'rand', 'sicherheitsabstand', 'druckrand'],
    answer:
      'Der **Beschnitt** (auch Anschnitt oder Bleed) ist der Bereich, der beim Drucken über das Endformat hinausgeht und nach dem Druck abgeschnitten wird. Er sorgt dafür, dass keine weißen Ränder entstehen. Du kannst den Beschnitt im Produkt-Panel über den Schieberegler einstellen (0–5 cm). Die rote gestrichelte Linie auf der Leinwand zeigt den Beschnittbereich.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="panel-bleed"]', label: 'Beschnitt-Einstellung' },
    ],
  },

  // ── Finishing / Verarbeitung ──
  {
    id: 'finishing',
    keywords: ['verarbeitung', 'finishing', 'konfektionierung', 'veredelung', 'saum', 'oesen', 'hohlsaum', 'keder', 'klett', 'schneiden', 'konfektion'],
    answer:
      'Unter **Verarbeitung** findest du alle Optionen zur Konfektionierung deines Druckprodukts:\n\n• **Schneiden** – einfacher Zuschnitt\n• **Ösen** – Metallösen zur Befestigung\n• **Saum** – geschweißter Rand\n• **Saum + Ösen** – Kombination (beliebteste Option)\n• **Hohlsaum** – Tunnel für Stangen/Rohre\n• **Klettverschluss** – zur Rahmenbefestigung\n• **Keder** – Silikonkeder für Rahmenspannung\n\nKlicke auf "Verarbeitung" im rechten Panel.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-finishing"]', label: 'Verarbeitung öffnen' },
      { type: 'open', panel: 'finishing' },
    ],
  },

  // ── Grommets / Ösen ──
  {
    id: 'grommets',
    keywords: ['oese', 'oesen', 'grommet', 'nickel', 'nickeloese', 'befestigung oese', 'metall ring'],
    answer:
      '**Ösen** (Nickelösen) sind Metallringe, die in das Banner gestanzt werden. Sie dienen zur Befestigung mit Kabelbindern, Haken oder Seilen. Du kannst Ösen in verschiedenen Abständen platzieren lassen (alle 20–50 cm oder nur in den Ecken) und zwischen 11 mm und 13 mm Durchmesser wählen. Öffne dazu die Verarbeitung im Produkt-Panel.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-finishing"]', label: 'Verarbeitung → Ösen' },
      { type: 'open', panel: 'finishing' },
    ],
  },

  // ── Hem / Saum ──
  {
    id: 'hem',
    keywords: ['saum', 'saeumen', 'schweissen', 'saumbreite', 'randsaum', 'hem', 'umschlag'],
    answer:
      'Der **Saum** ist ein umgeschlagener und verschweißter Rand, der das Banner verstärkt und ein Ausreißen verhindert. Typische Saumbreiten sind 3–5 cm. Der Saum kann mit Ösen kombiniert werden. In der Verarbeitung kannst du den Saum pro Seite oder für alle Seiten gleichzeitig einstellen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-finishing"]', label: 'Verarbeitung → Saum' },
      { type: 'open', panel: 'finishing' },
    ],
  },

  // ── Hohlsaum ──
  {
    id: 'hohlsaum',
    keywords: ['hohlsaum', 'tunnel', 'tunnelsaum', 'stange', 'durchzug', 'rohr', 'aufhaengung'],
    answer:
      'Der **Hohlsaum** (Tunnelsaum) ist ein geschweißter Tunnel am Rand des Banners. Durch diesen Tunnel kann eine Stange oder ein Rohr geschoben werden – ideal für Aufhängungen oder zum Spannen. Die Breite beträgt mindestens 4 cm. Du kannst wählen, ob die Enden offen oder geschlossen sein sollen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-finishing"]', label: 'Verarbeitung → Hohlsaum' },
      { type: 'open', panel: 'finishing' },
    ],
  },

  // ── Keder ──
  {
    id: 'keder',
    keywords: ['keder', 'kederschiene', 'rahmen', 'spannrahmen', 'silikon'],
    answer:
      'Der **Keder** ist ein rundes Silikonprofil (ca. 7 mm), das in die Kante des Drucks eingeschweißt wird. Damit lässt sich das Banner in Keder-Klemmschienen oder Spannrahmen einspannen. Öffne die Verarbeitung für diese Option.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-finishing"]', label: 'Verarbeitung → Keder' },
      { type: 'open', panel: 'finishing' },
    ],
  },

  // ── Save ──
  {
    id: 'save',
    keywords: ['speichern', 'sichern', 'save', 'abspeichern', 'design speichern', 'sicherung'],
    answer:
      'Klicke auf **"Design speichern"** im rechten Produkt-Panel. Dein Design wird auf dem Server gespeichert und du erhältst eine Design-ID. Wenn du das Design später wieder öffnest, werden alle Objekte wiederhergestellt.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-save"]', label: 'Design speichern' },
    ],
  },

  // ── Cart ──
  {
    id: 'cart',
    keywords: ['warenkorb', 'bestellen', 'kaufen', 'cart', 'bestellung', 'in den warenkorb'],
    answer:
      'Klicke auf **"In den Warenkorb"** im rechten Produkt-Panel. Das Design wird automatisch gespeichert und in den Warenkorb gelegt. Von dort aus kannst du die Bestellung abschließen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-cart"]', label: 'In den Warenkorb' },
    ],
  },

  // ── Zoom ──
  {
    id: 'zoom',
    keywords: ['zoom', 'vergroessern', 'verkleinern', 'rein zoomen', 'raus zoomen', 'ansicht', 'einpassen', 'skalieren ansicht'],
    answer:
      'Du kannst auf verschiedene Weisen zoomen:\n\n• **Strg/Cmd + Mausrad** – Rein- und Rauszoomen\n• **Zoom-Leiste** unten am Bildschirm – Schieberegler verwenden\n• **"Einpassen"** – klicke auf den Button rechts in der Zoom-Leiste, um die Leinwand auf den Bildschirm einzupassen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-grid"]', label: 'Zoom-Bereich (oben in der Toolbar)' },
    ],
  },

  // ── Layers ──
  {
    id: 'layers',
    keywords: ['ebene', 'ebenen', 'layer', 'layers', 'reihenfolge', 'z-order', 'vordergrund', 'hintergrund ebene', 'anordnung'],
    answer:
      'Im **Ebenen-Panel** (rechts unten) siehst du alle Objekte als Liste. Du kannst:\n\n• Objekte durch Klicken auswählen\n• Die Sichtbarkeit mit dem Auge-Symbol umschalten\n• Objekte sperren (Schloss-Symbol)\n• Objekte löschen\n\nIn der Toolbar oben findest du Buttons, um ein Objekt eine Ebene nach vorne oder hinten zu verschieben.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="panel-layers"]', label: 'Ebenen-Panel' },
    ],
  },

  // ── Undo ──
  {
    id: 'undo',
    keywords: ['rueckgaengig', 'undo', 'zurueck', 'zuruecknehmen', 'strg z', 'ctrl z', 'fehler rueckgaengig'],
    answer:
      'Drücke **Strg+Z** (oder Cmd+Z auf Mac), um die letzte Aktion rückgängig zu machen. Du kannst auch den **Rückgängig-Button** oben links in der Toolbar nutzen. Mit Strg+Y oder Strg+Shift+Z kannst du wiederhergestellt Aktionen wiederherstellen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-undo"]', label: 'Rückgängig' },
    ],
  },

  // ── Redo ──
  {
    id: 'redo',
    keywords: ['wiederherstellen', 'redo', 'strg y', 'ctrl y', 'nochmal'],
    answer:
      'Drücke **Strg+Y** oder **Strg+Shift+Z** (Cmd auf Mac), um eine rückgängig gemachte Aktion wiederherzustellen. Du findest den Wiederherstellen-Button auch oben in der Toolbar.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-redo"]', label: 'Wiederherstellen' },
    ],
  },

  // ── Rotate ──
  {
    id: 'rotate',
    keywords: ['drehen', 'rotation', 'rotieren', 'grad', 'winkel', '90 grad', 'ausrichten drehen'],
    answer:
      'Um ein Objekt zu drehen:\n\n• **Frei drehen**: Bewege die Maus über das runde Symbol oben rechts am ausgewählten Objekt und ziehe es.\n• **90° drehen**: Klicke auf den Dreh-Button oben in der Toolbar.\n• Halte **Shift** gedrückt, um in 15°-Schritten zu drehen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-rotate"]', label: 'Drehen (90°)' },
    ],
  },

  // ── Duplicate ──
  {
    id: 'duplicate',
    keywords: ['duplizieren', 'kopieren', 'kopie', 'duplicate', 'clone', 'klonen', 'verdoppeln', 'strg d', 'ctrl d'],
    answer:
      'Wähle ein Objekt aus und drücke **Strg+D** (Cmd+D auf Mac). Eine Kopie wird leicht versetzt auf der Leinwand platziert. Du kannst auch den Duplizieren-Button in der oberen Toolbar verwenden.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-duplicate"]', label: 'Duplizieren' },
    ],
  },

  // ── Delete ──
  {
    id: 'delete',
    keywords: ['loeschen', 'entfernen', 'delete', 'entf', 'backspace', 'weg', 'objekt loeschen', 'element entfernen'],
    answer:
      'Wähle das Objekt aus und drücke die **Entf-Taste** (Delete) oder **Backspace**. Alternativ kannst du den Löschen-Button in der oberen Toolbar oder im Ebenen-Panel nutzen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-delete"]', label: 'Löschen' },
    ],
  },

  // ── Group ──
  {
    id: 'group',
    keywords: ['gruppieren', 'gruppe', 'group', 'zusammenfassen', 'strg g', 'ctrl g', 'mehrere objekte'],
    answer:
      'Wähle mehrere Objekte aus (Shift+Klick oder Rahmen ziehen) und drücke **Strg+G** (Cmd+G auf Mac) zum Gruppieren. Mit **Strg+Shift+G** löst du die Gruppierung wieder auf. Gruppierte Objekte lassen sich gemeinsam verschieben, skalieren und drehen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-group"]', label: 'Gruppieren' },
    ],
  },

  // ── Preview ──
  {
    id: 'preview',
    keywords: ['vorschau', 'preview', 'ansicht', 'druckvorschau', 'so sieht es aus', 'ergebnis'],
    answer:
      'Klicke auf das **Augen-Symbol** oben in der Toolbar, um die Vorschau zu aktivieren. In der Vorschau werden Hilfslinien, Raster und Beschnittmarkierungen ausgeblendet, sodass du das fertige Druckergebnis siehst.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-preview"]', label: 'Vorschau' },
    ],
  },

  // ── Grid ──
  {
    id: 'grid',
    keywords: ['raster', 'grid', 'gitternetz', 'gitter', 'hilfslinien raster', 'einrasten'],
    answer:
      'Klicke auf das **Raster-Symbol** oben in der Toolbar, um das Gitternetz ein- oder auszublenden. Das Raster hilft dir bei der präzisen Ausrichtung von Objekten.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-grid"]', label: 'Raster ein/aus' },
    ],
  },

  // ── Rulers ──
  {
    id: 'rulers',
    keywords: ['lineal', 'lineale', 'ruler', 'massband', 'mass', 'massstab', 'cm anzeige'],
    answer:
      'Klicke auf das **Lineal-Symbol** oben in der Toolbar, um die Lineale am oberen und linken Rand ein- oder auszublenden. Die Lineale zeigen die Maße in Zentimetern an.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-rulers"]', label: 'Lineale ein/aus' },
    ],
  },

  // ── Keyboard shortcuts ──
  {
    id: 'shortcuts',
    keywords: ['tastenkuerzel', 'shortcut', 'tastatur', 'hotkey', 'keyboard', 'kurzbefehl', 'schnelltaste', 'tastenkombination'],
    answer:
      '**Wichtige Tastenkürzel:**\n\n• **Strg+Z** – Rückgängig\n• **Strg+Y** / **Strg+Shift+Z** – Wiederherstellen\n• **Strg+D** – Duplizieren\n• **Strg+G** – Gruppieren\n• **Strg+Shift+G** – Gruppierung aufheben\n• **Entf / Backspace** – Löschen\n• **Escape** – Auswahl aufheben\n• **Strg+Mausrad** – Zoomen\n• **Shift beim Drehen** – 15°-Schritte',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-undo"]', label: 'Tastenkürzel – Toolbar oben' },
    ],
  },

  // ── General overview ──
  {
    id: 'overview',
    keywords: ['was kann ich', 'hilfe', 'help', 'uebersicht', 'funktionen', 'was geht', 'moeglichkeiten', 'einfuehrung', 'tutorial', 'anfaenger', 'start'],
    answer:
      '**Willkommen im Selenny Designer!** Hier eine Übersicht der Funktionen:\n\n🎨 **Gestalten**: Text, Formen, Bilder und QR-Codes einfügen\n📐 **Anpassen**: Größe, Beschnitt und Verarbeitung konfigurieren\n🔧 **Bearbeiten**: Objekte verschieben, drehen, skalieren, gruppieren\n👁️ **Vorschau**: Fertiges Druckergebnis ansehen\n💾 **Speichern**: Design sichern und in den Warenkorb legen\n\nFrag mich gerne zu einem bestimmten Thema – z.B. "Wie füge ich Text ein?" oder "Was ist Beschnitt?"',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-text"]', label: 'Werkzeuge links – loslegen!' },
    ],
  },

  // ── Align ──
  {
    id: 'align',
    keywords: ['ausrichten', 'zentrieren', 'mittig', 'links ausrichten', 'rechts ausrichten', 'oben ausrichten', 'unten ausrichten', 'alignment', 'horizontal', 'vertikal'],
    answer:
      'Wähle ein Objekt aus und nutze die **Ausrichtungs-Buttons** in der oberen Toolbar: Links, Horizontal Mitte, Rechts, Oben, Vertikal Mitte, Unten. Die Ausrichtung bezieht sich auf die Leinwand.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-align-left"]', label: 'Ausrichten (Toolbar oben)' },
    ],
  },

  // ── Flip ──
  {
    id: 'flip',
    keywords: ['spiegeln', 'flip', 'horizontal spiegeln', 'vertikal spiegeln', 'umdrehen', 'umkehren'],
    answer:
      'Wähle ein Objekt aus und klicke auf den **Spiegeln-Button** in der oberen Toolbar. Du kannst horizontal (links-rechts) oder vertikal (oben-unten) spiegeln.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-flip-h"]', label: 'Spiegeln' },
    ],
  },

  // ── Lock ──
  {
    id: 'lock',
    keywords: ['sperren', 'lock', 'fixieren', 'entsperren', 'unveraenderlich', 'schuetzen', 'festsetzen'],
    answer:
      'Wähle ein Objekt aus und klicke auf das **Schloss-Symbol** in der oberen Toolbar (oder im Ebenen-Panel). Ein gesperrtes Objekt kann nicht verschoben, skaliert oder bearbeitet werden – ideal, um versehentliche Änderungen zu verhindern.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-lock"]', label: 'Sperren / Entsperren' },
    ],
  },

  // ── Data check ──
  {
    id: 'datacheck',
    keywords: ['datencheck', 'datenpruefung', 'pruefung', 'express', 'standard', 'druckdaten', 'qualitaet'],
    answer:
      'Der **Datencheck** prüft dein Design auf Druckfähigkeit (Auflösung, Farbprofil, Beschnitt etc.):\n\n• **Standard** (2–3 Werktage) – gründliche Prüfung\n• **Express** (1 Werktag) – schnelle Prüfung (+15 EUR)\n• **Kein Datencheck** – du übernimmst die Verantwortung\n\nDu findest die Auswahl im rechten Produkt-Panel.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="panel-dimensions"]', label: 'Produkt-Panel (rechts)' },
    ],
  },

  // ── Text effects ──
  {
    id: 'text-effects',
    keywords: ['texteffekt', 'effekt', 'neigung', 'skew', 'perspektive', 'dehnen', 'verzerren', 'textverformung'],
    answer:
      'Wähle einen Text auf der Leinwand aus und öffne den Text-Dialog (T-Symbol links). Wechsle zum Tab **"Effekte"**. Dort findest du Neigung, Perspektive, Dehnung und mehr. Außerdem kannst du Skew X/Y und Scale manuell per Schieberegler einstellen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-text"]', label: 'Text → Effekte' },
      { type: 'open', panel: 'text' },
    ],
  },

  // ── Move objects ──
  {
    id: 'move',
    keywords: ['verschieben', 'bewegen', 'position', 'platzieren', 'drag', 'ziehen'],
    answer:
      'Klicke auf ein Objekt, um es auszuwählen, und **ziehe es** mit gedrückter Maustaste an die gewünschte Position. Für präzises Platzieren kannst du das Raster aktivieren – Objekte rasten dann automatisch ein.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-grid"]', label: 'Raster für präzises Platzieren' },
    ],
  },

  // ── Scale objects ──
  {
    id: 'scale',
    keywords: ['skalieren', 'groesse aendern', 'vergroessern objekt', 'verkleinern objekt', 'resize', 'ecke ziehen'],
    answer:
      'Wähle ein Objekt aus und ziehe an den **Eckpunkten**, um es zu skalieren. Halte **Shift** gedrückt, um die Proportionen beizubehalten. In den Eigenschaften (rechtes Panel) kannst du auch exakte Werte eingeben.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-align-left"]', label: 'Toolbar oben – Objekt bearbeiten' },
    ],
  },

  // ── Color ──
  {
    id: 'color',
    keywords: ['farbe', 'farbe aendern', 'objektfarbe', 'fuellfarbe', 'fill', 'farbwahl', 'color picker'],
    answer:
      'Wähle ein Objekt aus – im **Eigenschaften-Panel** rechts (erscheint bei Auswahl) kannst du die Füllfarbe, Rahmenfarbe und Deckkraft ändern. Bei Textobjekten kannst du die Textfarbe auch im Text-Dialog einstellen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-bgcolor"]', label: 'Farbe ändern' },
    ],
  },

  // ── Klettverschluss ──
  {
    id: 'klett',
    keywords: ['klett', 'klettverschluss', 'velcro', 'klettband'],
    answer:
      '**Klettverschluss** wird an den Rand des Banners genäht oder geschweißt. Es gibt Vorder- und Rückseiten-Varianten in Weiß oder Schwarz. Ideal für Rahmensysteme oder wenn das Banner häufig gewechselt wird. Wähle diese Option in der Verarbeitung.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-finishing"]', label: 'Verarbeitung → Klett' },
      { type: 'open', panel: 'finishing' },
    ],
  },

  // ── Views ──
  {
    id: 'views',
    keywords: ['ansicht', 'seite', 'vorderseite', 'rueckseite', 'view', 'mehrseitig', 'seiten'],
    answer:
      'Am unteren Rand der Leinwand findest du die **Ansichten-Tabs**. Damit kannst du zwischen verschiedenen Seiten (z.B. Vorderseite/Rückseite) wechseln. Jede Ansicht hat eigene Objekte und Einstellungen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-preview"]', label: 'Ansichten (unten am Canvas)' },
    ],
  },

  // ── Delivery ──
  {
    id: 'delivery',
    keywords: ['lieferzeit', 'lieferung', 'versand', 'werktage', 'liefern', 'dauer', 'wann kommt'],
    answer:
      'Die Lieferzeit hängt vom gewählten Datencheck ab. Standard-Datencheck: ca. 5–6 Werktage. Express-Datencheck: ca. 4 Werktage. Ohne Datencheck: ca. 3 Werktage. Die genaue Lieferzeit wird im Produkt-Panel angezeigt.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-cart"]', label: 'Produkt-Panel (rechts)' },
    ],
  },

  // ── Price ──
  {
    id: 'price',
    keywords: ['preis', 'kosten', 'berechnung', 'flaeche', 'quadratmeter', 'euro', 'eur', 'was kostet'],
    answer:
      'Der Preis berechnet sich nach **Fläche** (Breite x Höhe in m²) multipliziert mit dem Quadratmeterpreis, plus eventuelle Zuschläge für Datencheck und Verarbeitung. Die aktuelle Preisberechnung siehst du im rechten Produkt-Panel.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-save"]', label: 'Produkt-Panel (rechts)' },
    ],
  },

  // ── Cliparts ──
  {
    id: 'cliparts',
    keywords: ['clipart', 'cliparts', 'clip art', 'icon', 'icons', 'symbol', 'symbole', 'sticker', 'grafik einfuegen', 'dekor', 'dekoration', 'verzierung', 'schmuck'],
    answer:
      'Klicke auf **„Cliparts"** in der linken Werkzeugleiste. Dort findest du eine Sammlung von vorgefertigten Grafiken und Icons, sortiert nach Kategorien. Einfach auf ein Clipart klicken – es wird sofort auf der Leinwand platziert.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-cliparts"]', label: 'Cliparts einfügen' },
      { type: 'open', panel: 'cliparts' },
    ],
  },

  // ── Background Image ──
  {
    id: 'bgimage',
    keywords: ['hintergrundbild', 'hintergrund bild', 'background image', 'bild hintergrund', 'foto hintergrund', 'vollflaeche', 'ganzes bild'],
    answer:
      'Klicke auf **„Hintergrundbild"** in der linken Werkzeugleiste. Du hast zwei Tabs: **„Eigene Datei"** zum Hochladen oder **„Pixabay"** für kostenlose Fotos als Hintergrund. Das Bild liegt immer auf der hintersten Ebene. Zum Entfernen nutze den roten Button im Panel oder den **schwimmenden Button** unten links auf der Leinwand.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-bgimage"]', label: 'Hintergrundbild' },
      { type: 'open', panel: 'bgimage' },
    ],
  },

  // ── Remove Background Image ──
  {
    id: 'bgimage-remove',
    keywords: ['hintergrundbild entfernen', 'hintergrundbild loeschen', 'hintergrund entfernen', 'hintergrund loeschen', 'hintergrund weiss', 'hintergrund zuruecksetzen', 'hintergrund weg', 'bild entfernen hintergrund', 'background remove'],
    answer:
      'Es gibt zwei Wege, das Hintergrundbild zu entfernen:\n\n1. **Schwimmender Button** unten links auf der Leinwand: „Hintergrundbild entfernen" – er erscheint automatisch, sobald ein Hintergrundbild gesetzt ist.\n2. **Hintergrundbild-Panel** in der linken Werkzeugleiste → Tab „Eigene Datei" → roter Button „Hintergrundbild entfernen".\n\nBeim Entfernen wird die Hintergrundfarbe automatisch auf **Weiß** zurückgesetzt.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-bgimage"]', label: 'Hintergrundbild entfernen' },
      { type: 'open', panel: 'bgimage' },
    ],
  },

  // ── Pixabay / Stock Photos ──
  {
    id: 'pixabay',
    keywords: ['pixabay', 'stockfoto', 'stockbild', 'kostenloses bild', 'gratis foto', 'freie bilder', 'bildsuche', 'fotobibliothek', 'bildarchiv', 'suche bild'],
    answer:
      'Es gibt **zwei Pixabay-Zugänge**:\n\n1. **Als Bildobjekt:** Klicke auf „Bilder" → „Kostenlose Fotos (Pixabay)" – das Bild wird als verschiebbares Objekt eingefügt.\n2. **Als Hintergrundbild:** Klicke auf „Hintergrundbild" → Tab „Pixabay" – das Bild wird als Vollflächenhintergrund auf der hintersten Ebene eingesetzt.\n\nDu kannst nach Stichwörtern suchen und aus Millionen lizenzfreier Fotos wählen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-image"]', label: 'Bilder → Pixabay' },
      { type: 'open', panel: 'pixabay' },
    ],
  },

  // ── Snap lines / Magnetic alignment ──
  {
    id: 'snaplines',
    keywords: ['magnetisch', 'magnetlinie', 'snap', 'snapline', 'einrasten', 'automatisch ausrichten', 'hilfslinie', 'hilfslinien', 'fuehrungslinie', 'guide'],
    answer:
      'Wenn du ein Objekt verschiebst, erscheinen automatisch **magnetische Hilfslinien** (grün für Kanten/Mitte der Leinwand, lila für Ausrichtung an anderen Objekten). Die Objekte rasten sanft ein, wenn sie an einer Linie ankommen. Das Raster verstärkt diesen Effekt zusätzlich.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-grid"]', label: 'Raster für stärkeres Einrasten' },
    ],
  },

  // ── Drag & Drop ──
  {
    id: 'dragdrop',
    keywords: ['drag', 'drop', 'ziehen', 'ablegen', 'drag and drop', 'hereinziehen', 'reinziehen'],
    answer:
      'Du kannst **Bilder per Drag & Drop** direkt vom Desktop oder Dateimanager auf die Leinwand ziehen. Bei Collagen kannst du Fotos auch direkt auf die Platzhalter ziehen. Objekte auf der Leinwand verschiebst du durch Klicken und Ziehen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-image"]', label: 'Bilder einfügen' },
    ],
  },

  // ── Transparency / Opacity ──
  {
    id: 'opacity',
    keywords: ['transparenz', 'durchsichtig', 'opacity', 'deckkraft', 'transparent', 'halbtransparent', 'unsichtbar'],
    answer:
      'Wähle ein Objekt aus – im **Eigenschaften-Panel** (erscheint rechts) findest du den Regler **„Deckkraft"**. Stelle die Deckkraft zwischen 0% (unsichtbar) und 100% (voll sichtbar) ein. Ideal für Wasserzeichen oder Overlay-Effekte.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-layers"]', label: 'Objekt auswählen → Eigenschaften' },
    ],
  },

  // ── Fonts / Typography ──
  {
    id: 'fonts',
    keywords: ['schriftart', 'schrift', 'font', 'google fonts', 'schrifttyp', 'typografie', 'typography', 'buchstaben', 'arial', 'roboto', 'lobster', 'handschrift'],
    answer:
      'Über 100 **Google Fonts** stehen dir zur Verfügung! Klicke auf „Text" in der Werkzeugleiste oder wähle einen Text auf der Leinwand aus. Im Text-Dialog oder im Eigenschaften-Panel kannst du die Schriftart über ein Dropdown mit **Live-Vorschau** auswählen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-text"]', label: 'Text → Schriftart wählen' },
      { type: 'open', panel: 'text' },
    ],
  },

  // ── Text color / stroke ──
  {
    id: 'text-color',
    keywords: ['textfarbe', 'schriftfarbe', 'text farbe', 'buchstabenfarbe', 'textumrandung', 'text outline', 'text kontur', 'text stroke'],
    answer:
      'Wähle einen Text aus → im **Eigenschaften-Panel** rechts kannst du die **Füllfarbe** (Textfarbe) und **Rahmenfarbe** (Kontur/Outline) ändern. Mit der Rahmenbreite stellst du die Stärke der Kontur ein – ideal für Texte auf unruhigem Hintergrund.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-text"]', label: 'Text auswählen → Eigenschaften' },
    ],
  },

  // ── Image filters ──
  {
    id: 'image-filters',
    keywords: ['bildfilter', 'filter', 'helligkeit', 'kontrast', 'brightness', 'contrast', 'bild bearbeiten', 'bildbearbeitung', 'korrektur'],
    answer:
      'Wähle ein **Bild** auf der Leinwand aus. Im **Eigenschaften-Panel** (erscheint rechts) findest du den Bereich **„Filter"** mit Reglern für Helligkeit und Kontrast. Die Änderungen werden sofort auf das Bild angewendet.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-image"]', label: 'Bild auswählen → Filter' },
    ],
  },

  // ── Banner types / Material ──
  {
    id: 'material',
    keywords: ['material', 'banner', 'plane', 'pvc', 'mesh', 'stoff', 'folie', 'vinyl', 'backlit', 'frontlit', 'blockout', 'netzvinyl', 'meshbanner'],
    answer:
      'Die gängigsten **Banner-Materialien** sind:\n\n• **PVC Frontlit** – Standardbanner (440–510 g/m²), wetterfest, ideal für Außenwerbung\n• **PVC Mesh** – Netzvinyl mit Windlöchern, ideal für große Fassadenbanner\n• **PVC Backlit** – Durchleuchtbar für Leuchtkästen\n• **Stoff** – Textilbanner, ideal für Messen und Innenräume\n• **Blockout** – Beidseitig bedruckbar, blickdicht\n\nDas Material wählst du im Produkt-Panel.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="panel-dimensions"]', label: 'Produkt-Panel (Material)' },
    ],
  },

  // ── Resolution / DPI ──
  {
    id: 'resolution',
    keywords: ['aufloesung', 'dpi', 'ppi', 'druckqualitaet', 'bildqualitaet', 'pixelanzahl', 'unscharf', 'verpixelt', 'zu klein'],
    answer:
      'Für den **Großformatdruck** reichen in der Regel **72–150 DPI** (bei Betrachtungsabstand ab 1 m). Für Nahbetrachtung empfehlen wir mindestens **150 DPI**. Wenn ein Bild beim Vergrößern verpixelt aussieht, ist die Auflösung zu gering – verwende dann ein größeres Ausgangsbild.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="panel-dimensions"]', label: 'Motivgröße beachten' },
    ],
  },

  // ── CMYK / Color profile ──
  {
    id: 'cmyk',
    keywords: ['cmyk', 'rgb', 'farbprofil', 'farbraum', 'druckfarben', 'farbabweichung', 'farbecht', 'farbmodus', 'icc'],
    answer:
      'Der Designer arbeitet im **RGB-Farbmodus** (wie alle Bildschirme). Beim Druck wird automatisch in **CMYK** umgerechnet. Sehr leuchtende RGB-Farben (Neongrün, leuchtendes Blau) können im Druck etwas gedeckter wirken. Tipp: Vermeide sehr dunkle Flächen mit 100% aller Farben – das kann zu Farbverläufen führen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-bgcolor"]', label: 'Farben im Designer' },
    ],
  },

  // ── Safe area / Important content ──
  {
    id: 'safe-area',
    keywords: ['sicherheitsbereich', 'safe area', 'sicherheitsabstand', 'rand freihalten', 'abstand rand', 'content bereich', 'druckbereich', 'wichtiger bereich'],
    answer:
      'Halte **wichtige Inhalte** (Text, Logos) mindestens **3–5 mm** vom Rand entfernt, damit sie beim Schneiden nicht angeschnitten werden. Der **Beschnitt-Bereich** (rot gestrichelt) wird nach dem Druck abgeschnitten – dort dürfen nur Hintergründe und unwichtige Inhalte liegen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="panel-bleed"]', label: 'Beschnitt-Einstellung' },
    ],
  },

  // ── Copy/Paste ──
  {
    id: 'copypaste',
    keywords: ['kopieren einfuegen', 'copy paste', 'strg c', 'strg v', 'ctrl c', 'ctrl v', 'zwischenablage', 'clipboard'],
    answer:
      'Wähle ein Objekt aus und drücke **Strg+C** zum Kopieren und **Strg+V** zum Einfügen. Alternativ nutze **Strg+D** zum schnellen Duplizieren – das erstellt sofort eine Kopie leicht versetzt auf der Leinwand.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-duplicate"]', label: 'Duplizieren / Kopieren' },
    ],
  },

  // ── Select multiple / Multi-select ──
  {
    id: 'multiselect',
    keywords: ['mehrere auswaehlen', 'multi select', 'mehrfachauswahl', 'shift klick', 'alle auswaehlen', 'rahmen ziehen', 'selektieren', 'markieren mehrere'],
    answer:
      'Du kannst **mehrere Objekte** auf verschiedene Arten auswählen:\n\n• **Shift+Klick** – Objekte nacheinander zur Auswahl hinzufügen\n• **Rahmen ziehen** – Klicke auf eine leere Stelle und ziehe einen Rahmen über die Objekte\n• **Strg+A** – Alle Objekte auswählen\n\nMit einer Mehrfachauswahl kannst du Objekte gemeinsam verschieben, gruppieren oder löschen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-group"]', label: 'Auswahl → Gruppieren' },
    ],
  },

  // ── Layer order ──
  {
    id: 'layer-order',
    keywords: ['reihenfolge', 'nach vorne', 'nach hinten', 'ueberlagern', 'ueberlappen', 'davor', 'dahinter', 'anordnung', 'z order', 'vordergrund hintergrund'],
    answer:
      'Wähle ein Objekt aus und nutze in der **oberen Toolbar** die Pfeil-Buttons:\n\n• **Pfeil hoch** – Objekt eine Ebene nach vorne\n• **Pfeil runter** – Objekt eine Ebene nach hinten\n\nIm **Ebenen-Panel** (links unten) siehst du die komplette Reihenfolge aller Objekte.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-layers"]', label: 'Ebenen-Panel' },
      { type: 'open', panel: 'layers' },
    ],
  },

  // ── Birthday banner ──
  {
    id: 'birthday',
    keywords: ['geburtstag banner', 'happy birthday', 'geburtstagsfeier', 'party banner', 'feier', 'jubilaeum', 'jubilaeumsbanner'],
    answer:
      'Für ein **Geburtstagsbanner** empfehlen wir:\n\n1. **Hintergrundbild** oder Farbe setzen\n2. **Zahlencollage** mit dem Alter (z.B. 50) – Fotos in Zahlenform!\n3. **Text** mit Glückwünschen hinzufügen\n4. **Cliparts** für Dekoration (Luftballons, Sterne)\n\nTipp: Nutze die Zahlencollage unter „Collagen Layouts"!',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-collage"]', label: 'Collagen → Zahlencollage' },
      { type: 'open', panel: 'collage' },
    ],
  },

  // ── Wedding banner ──
  {
    id: 'wedding',
    keywords: ['hochzeit', 'wedding', 'trauung', 'braut', 'braeutigam', 'hochzeitsbanner', 'liebe', 'heiraten'],
    answer:
      'Für ein **Hochzeitsbanner**: Wähle ein elegantes Hintergrundbild, füge die Namen des Paares als Text hinzu (z.B. mit der Schriftart „Parisienne" oder „Dancing Script"), und ergänze mit einem Herz-Clipart oder einer Fotocollage mit Paarfotos.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-text"]', label: 'Text einfügen' },
    ],
  },

  // ── Business / Advertising banner ──
  {
    id: 'business',
    keywords: ['werbung', 'werbebanner', 'firmenbanner', 'business', 'logo einfuegen', 'firmenlogo', 'geschaeft', 'messe', 'messebanner', 'aussteller'],
    answer:
      'Für ein **Werbebanner**: Lade dein Firmenlogo als Bild hoch, füge Kontaktdaten als Text ein, und wähle eine auffällige Hintergrundfarbe. Für Messen empfehlen wir Stoffbanner (leicht, faltbar). Achte auf gut lesbare Schrift – Mindestgröße für Fernsicht: 72px+.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-image"]', label: 'Logo hochladen' },
    ],
  },

  // ── Construction banner / Bauzaunbanner ──
  {
    id: 'bauzaun',
    keywords: ['bauzaun', 'bauzaunbanner', 'baustelle', 'baustellenbanner', 'zaun', 'absperrung', 'sichtschutz'],
    answer:
      'Für **Bauzaunbanner** empfehlen wir PVC-Mesh (winddurchlässig) oder PVC Frontlit. Standardgröße: **340 × 173 cm** (passend für Standard-Bauzaunelemente). Vergiss die Ösen nicht – alle 50 cm für sichere Befestigung. Setze den Beschnitt auf mindestens 2 cm.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="panel-dimensions"]', label: 'Maße einstellen' },
    ],
  },

  // ── Rollup ──
  {
    id: 'rollup',
    keywords: ['rollup', 'roll up', 'roll-up', 'aufsteller', 'display', 'messedisplay', 'retractable'],
    answer:
      'Für **Roll-Up Displays**: Typische Größen sind 85 × 200 cm oder 100 × 200 cm. Wichtig: Der untere Bereich (ca. 15 cm) verschwindet in der Kassette – platziere dort keine wichtigen Inhalte. Wähle das passende Format im Produkt-Panel.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="panel-dimensions"]', label: 'Roll-Up Maße' },
    ],
  },

  // ── Poster ──
  {
    id: 'poster',
    keywords: ['poster', 'plakat', 'affiche', 'wandposter', 'fotoposter', 'kunstdruck'],
    answer:
      'Für **Poster** wähle die gewünschte Größe im Produkt-Panel (DIN A4 bis A0 oder Freiformat). Setze den Beschnitt auf 3 mm. Für Fotoposter: Lade ein hochauflösendes Bild als Hintergrundbild hoch. Für Textposter: Nutze auffällige Schriftarten und starke Kontraste.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="panel-dimensions"]', label: 'Postergröße wählen' },
    ],
  },

  // ── Snap / Magnet off ──
  {
    id: 'snap-off',
    keywords: ['magnet aus', 'einrasten aus', 'snap aus', 'frei bewegen', 'nicht einrasten', 'ohne magnet'],
    answer:
      'Die magnetischen Hilfslinien sind immer aktiv und helfen dir beim präzisen Platzieren. Wenn du ein Objekt **frei bewegen** möchtest, halte die **Alt-Taste** gedrückt – dann wird das Einrasten vorübergehend deaktiviert.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-grid"]', label: 'Raster & Hilfslinien' },
    ],
  },

  // ── Assistant / Help ──
  {
    id: 'assistant',
    keywords: ['assistent', 'hilfe fragen', 'chatbot', 'ki hilfe', 'designer assistent', 'frage stellen', 'support', 'wie geht', 'anleitung'],
    answer:
      'Du sprichst gerade mit mir – dem **Designer-Assistenten**! Du kannst jederzeit Fragen stellen, z.B. „Wie füge ich Text ein?" oder „Was ist Beschnitt?". Ich zeige dir dann genau, wo du klicken musst. Du kannst auch das **Mikrofon** nutzen und per Sprache fragen!',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-assistant"]', label: 'KI-Hilfe (das bin ich!)' },
    ],
  },

  // ── Onboarding / Tour ──
  {
    id: 'onboarding',
    keywords: ['einfuehrung', 'tour', 'rundgang', 'onboarding', 'erste schritte', 'anfangen', 'willkommen', 'neu hier', 'wie starte ich'],
    answer:
      'Beim ersten Besuch erscheint eine **interaktive Einführung**, die dich Schritt für Schritt durch alle Werkzeuge führt. Du kannst die Tour jederzeit über den „Hilfe & Fragen"-Button erneut starten. Frag mich einfach zu einem bestimmten Thema!',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-assistant"]', label: 'Einführung starten' },
    ],
  },

  // ── Quantity ──
  {
    id: 'quantity',
    keywords: ['menge', 'anzahl', 'stueckzahl', 'exemplare', 'auflage', 'wieviele', 'stueck'],
    answer:
      'Die **Bestellmenge** stellst du im rechten Produkt-Panel ein. Je mehr Stück du bestellst, desto günstiger wird der Stückpreis. Die Mengenrabatte werden automatisch berechnet.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-cart"]', label: 'Produkt-Panel → Menge' },
    ],
  },

  // ── File formats ──
  {
    id: 'fileformat',
    keywords: ['dateiformat', 'jpg', 'png', 'svg', 'webp', 'pdf', 'format', 'welche formate', 'dateityp'],
    answer:
      'Unterstützte **Bildformate**: JPG, PNG, WebP und SVG. Für beste Qualität empfehlen wir PNG (verlustfrei) oder SVG (Vektorgrafik, unendlich skalierbar). JPG eignet sich für Fotos. SVG ist ideal für Logos und Grafiken.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-image"]', label: 'Bilder einfügen' },
    ],
  },

  // ── Curved text ──
  {
    id: 'curved-text',
    keywords: ['gebogener text', 'text biegen', 'text kurve', 'bogentext', 'kreistext', 'runder text', 'curved'],
    answer:
      'Öffne den **Text-Dialog** (T-Symbol links) und wechsle zum Tab **„Effekte"**. Dort findest du den Schieberegler für **gebogenen Text** (Kurve). Positive Werte biegen den Text nach oben, negative nach unten. Du kannst auch den Radius anpassen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-text"]', label: 'Text → Effekte → Kurve' },
      { type: 'open', panel: 'text' },
    ],
  },

  // ── Photo collage layouts ──
  {
    id: 'collage-layouts',
    keywords: ['collage layout', 'fotoraster', 'bildanordnung', 'fotos anordnen', 'bildraster', 'layoutvorlage', 'fotovorlage', 'wieviele fotos'],
    answer:
      'Es stehen Layouts für **1 bis 12+ Fotos** zur Verfügung – von einfachen Rastern bis zu kreativen Anordnungen. Klicke auf „Collagen Layouts" → wähle ein Layout → klicke „Layout anwenden". Danach kannst du über „Fotos zuordnen" deine Bilder auf die Platzhalter verteilen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="tool-collage"]', label: 'Collagen Layouts' },
      { type: 'open', panel: 'collage' },
    ],
  },

  // ── Outdoor / Weather ──
  {
    id: 'outdoor',
    keywords: ['aussen', 'outdoor', 'wetterfest', 'regen', 'wind', 'sonne', 'uv', 'haltbarkeit', 'wetterbstaendig', 'draussen'],
    answer:
      'Unsere PVC-Banner sind **wetterfest**, UV-beständig und für den Außeneinsatz geeignet. Für winddurchlässige Banner wähle Mesh-Material. Für maximale Haltbarkeit empfehlen wir verschweißten Saum + Ösen. Bei starkem Wind: größere Ösenabstände (alle 25–30 cm).',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-finishing"]', label: 'Verarbeitung für Outdoor' },
    ],
  },

  // ── Indoor ──
  {
    id: 'indoor',
    keywords: ['innen', 'indoor', 'innenraum', 'messe', 'laden', 'schaufenster', 'shop', 'buero'],
    answer:
      'Für **Indoor-Banner** empfehlen wir leichten Stoff oder dünnes PVC. Verarbeitung: einfacher Schnitt oder Keder für Rahmen. Saum + Ösen sind bei Inneneinsatz meist nicht nötig. Für Leuchtkästen: Backlit-Material wählen.',
    actions: [
      { type: 'spotlight', selector: '[data-guide-id="btn-finishing"]', label: 'Verarbeitung für Indoor' },
    ],
  },
];

// ────────────────────────────────────────────────────
// Related / follow-up topics per knowledge entry
// ────────────────────────────────────────────────────

const RELATED_MAP = {
  'add-text': ['text-effects', 'fonts', 'text-color'],
  'add-shapes': ['color', 'scale', 'rotate'],
  'upload-image': ['pixabay', 'image-filters', 'scale'],
  'collage': ['number-collage', 'collage-layouts', 'birthday'],
  'number-collage': ['collage', 'birthday', 'collage-layouts'],
  'qr-code': ['scale', 'move', 'layers'],
  'bgcolor': ['bgimage', 'color', 'preview'],
  'bgimage': ['bgimage-remove', 'bgcolor', 'pixabay', 'upload-image'],
  'bgimage-remove': ['bgimage', 'bgcolor', 'pixabay'],
  'dimensions': ['bleed', 'finishing', 'price'],
  'bleed': ['safe-area', 'finishing', 'dimensions'],
  'finishing': ['grommets', 'hem', 'hohlsaum'],
  'grommets': ['hem', 'finishing', 'outdoor'],
  'hem': ['grommets', 'hohlsaum', 'finishing'],
  'hohlsaum': ['finishing', 'hem', 'dimensions'],
  'keder': ['finishing', 'klett', 'indoor'],
  'save': ['cart', 'preview', 'overview'],
  'cart': ['save', 'price', 'delivery'],
  'zoom': ['grid', 'rulers', 'preview'],
  'layers': ['layer-order', 'lock', 'group'],
  'undo': ['redo', 'shortcuts', 'delete'],
  'redo': ['undo', 'shortcuts'],
  'rotate': ['scale', 'flip', 'align'],
  'duplicate': ['copypaste', 'group', 'multiselect'],
  'delete': ['undo', 'layers', 'lock'],
  'group': ['multiselect', 'layers', 'lock'],
  'preview': ['save', 'zoom', 'grid'],
  'grid': ['snaplines', 'rulers', 'align'],
  'rulers': ['grid', 'dimensions', 'zoom'],
  'shortcuts': ['copypaste', 'duplicate', 'group'],
  'overview': ['add-text', 'add-shapes', 'onboarding'],
  'align': ['snaplines', 'grid', 'move'],
  'flip': ['rotate', 'align', 'scale'],
  'lock': ['layers', 'group', 'delete'],
  'datacheck': ['save', 'delivery', 'resolution'],
  'text-effects': ['curved-text', 'add-text', 'fonts'],
  'move': ['snaplines', 'align', 'grid'],
  'scale': ['move', 'rotate', 'resolution'],
  'color': ['bgcolor', 'cmyk', 'text-color'],
  'klett': ['finishing', 'keder', 'indoor'],
  'views': ['preview', 'save', 'layers'],
  'delivery': ['datacheck', 'price', 'cart'],
  'price': ['delivery', 'dimensions', 'quantity'],
  'cliparts': ['upload-image', 'add-shapes', 'layers'],
  'pixabay': ['upload-image', 'bgimage', 'image-filters'],
  'snaplines': ['grid', 'align', 'snap-off'],
  'dragdrop': ['upload-image', 'collage', 'move'],
  'opacity': ['color', 'layers', 'image-filters'],
  'fonts': ['add-text', 'text-effects', 'text-color'],
  'text-color': ['color', 'add-text', 'fonts'],
  'image-filters': ['upload-image', 'opacity', 'scale'],
  'material': ['outdoor', 'indoor', 'finishing'],
  'resolution': ['dimensions', 'datacheck', 'scale'],
  'cmyk': ['color', 'resolution', 'datacheck'],
  'safe-area': ['bleed', 'dimensions', 'preview'],
  'copypaste': ['duplicate', 'shortcuts', 'multiselect'],
  'multiselect': ['group', 'align', 'delete'],
  'layer-order': ['layers', 'lock', 'group'],
  'birthday': ['number-collage', 'collage', 'wedding'],
  'wedding': ['birthday', 'fonts', 'cliparts'],
  'business': ['material', 'upload-image', 'dimensions'],
  'bauzaun': ['outdoor', 'material', 'dimensions'],
  'rollup': ['indoor', 'dimensions', 'material'],
  'poster': ['dimensions', 'resolution', 'bgimage'],
  'snap-off': ['snaplines', 'grid', 'move'],
  'assistant': ['onboarding', 'overview', 'shortcuts'],
  'onboarding': ['overview', 'assistant', 'add-text'],
  'quantity': ['price', 'cart', 'delivery'],
  'fileformat': ['upload-image', 'resolution', 'cliparts'],
  'curved-text': ['text-effects', 'add-text', 'fonts'],
  'collage-layouts': ['collage', 'number-collage', 'birthday'],
  'outdoor': ['material', 'finishing', 'grommets'],
  'indoor': ['material', 'keder', 'klett'],
};

// ────────────────────────────────────────────────────
// Matching engine
// ────────────────────────────────────────────────────

function __computeApiBase() {
  const loc = window.location;
  const pathParts = loc.pathname.split('/');
  let idx = pathParts.indexOf('selenny-designer');
  if (idx < 0) idx = pathParts.indexOf('designer');
  if (idx >= 0) return loc.origin + pathParts.slice(0, idx + 1).join('/') + '/backend/api';
  return process.env.REACT_APP_API_URL || '/designer/api';
}
const API_BASE = __computeApiBase();

class DesignerAssistant {
  constructor() {
    this.kb = KNOWLEDGE_BASE;
    this.relatedMap = RELATED_MAP;
    this._sessionId = this._generateSessionId();
    // Pre-normalise keywords for performance
    this._normalised = this.kb.map((entry) => ({
      ...entry,
      _nkw: entry.keywords.map(normalize),
    }));
  }

  _generateSessionId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 8);
  }

  /**
   * Log a question to the backend for analytics.
   */
  _logQuestion(question, normalized, matchedId, matchedScore, answerText) {
    try {
      fetch(`${API_BASE}/assistant/questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          question_normalized: normalized,
          matched_id: matchedId || null,
          matched_score: matchedScore || 0,
          answer_text: answerText || null,
          session_id: this._sessionId,
        }),
      }).catch(() => {});
    } catch (e) {
      // Logging should never break the assistant
    }
  }

  /**
   * Match a user query against the knowledge base.
   * @param {string} query - User input in German
   * @returns {{ answer: string, actions: Array, confidence: number, id: string, related: string[] }}
   */
  match(query) {
    const q = normalize(query);
    const qWords = q.split(' ').filter(Boolean);
    if (qWords.length === 0) return this._fallback();

    let bestScore = 0;
    let bestEntry = null;

    for (const entry of this._normalised) {
      let score = 0;

      for (const kw of entry._nkw) {
        // Exact substring match in query
        if (q.includes(kw)) {
          score += kw.length * 3; // weight longer keyword matches higher
        }
        // Partial word matches
        for (const w of qWords) {
          if (kw.startsWith(w) && w.length >= 3) {
            score += w.length * 2;
          } else if (kw.includes(w) && w.length >= 3) {
            score += w.length;
          } else if (w.startsWith(kw) && kw.length >= 3) {
            score += kw.length * 1.5;
          }
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestEntry = entry;
      }
    }

    if (!bestEntry || bestScore < 3) {
      this._logQuestion(query, q, null, bestScore, null);
      return this._fallback();
    }

    const confidence = Math.min(1, bestScore / 30);
    const related = (this.relatedMap[bestEntry.id] || []).slice(0, 3);

    this._logQuestion(query, q, bestEntry.id, bestScore, bestEntry.answer);

    return {
      answer: bestEntry.answer,
      actions: bestEntry.actions || [],
      confidence,
      id: bestEntry.id,
      related,
    };
  }

  /**
   * Get a knowledge entry by ID.
   */
  getById(id) {
    const entry = this.kb.find((e) => e.id === id);
    if (!entry) return null;
    const related = (this.relatedMap[id] || []).slice(0, 3);
    return {
      answer: entry.answer,
      actions: entry.actions || [],
      confidence: 1,
      id: entry.id,
      related,
    };
  }

  /**
   * Get a human-readable label for a KB entry ID (for chips).
   */
  getLabel(id) {
    const labels = {
      'add-text': 'Text einfügen',
      'add-shapes': 'Formen einfügen',
      'upload-image': 'Bild hochladen',
      'qr-code': 'QR-Code',
      'bgcolor': 'Hintergrundfarbe',
      'dimensions': 'Maße ändern',
      'bleed': 'Beschnitt',
      'finishing': 'Verarbeitung',
      'grommets': 'Ösen',
      'hem': 'Saum',
      'hohlsaum': 'Hohlsaum',
      'keder': 'Keder',
      'save': 'Speichern',
      'cart': 'Warenkorb',
      'zoom': 'Zoomen',
      'layers': 'Ebenen',
      'undo': 'Rückgängig',
      'redo': 'Wiederherstellen',
      'rotate': 'Drehen',
      'duplicate': 'Duplizieren',
      'delete': 'Löschen',
      'group': 'Gruppieren',
      'preview': 'Vorschau',
      'grid': 'Raster',
      'rulers': 'Lineale',
      'shortcuts': 'Tastenkürzel',
      'overview': 'Übersicht',
      'align': 'Ausrichten',
      'flip': 'Spiegeln',
      'lock': 'Sperren',
      'datacheck': 'Datencheck',
      'text-effects': 'Texteffekte',
      'move': 'Verschieben',
      'scale': 'Skalieren',
      'color': 'Farbe ändern',
      'klett': 'Klettverschluss',
      'views': 'Ansichten',
      'delivery': 'Lieferzeit',
      'price': 'Preisberechnung',
      'collage': 'Fotocollage',
      'number-collage': 'Zahlencollage',
      'cliparts': 'Cliparts',
      'bgimage': 'Hintergrundbild',
      'bgimage-remove': 'Hintergrundbild entfernen',
      'pixabay': 'Kostenlose Fotos',
      'snaplines': 'Magnetlinien',
      'dragdrop': 'Drag & Drop',
      'opacity': 'Transparenz',
      'fonts': 'Schriftarten',
      'text-color': 'Textfarbe',
      'image-filters': 'Bildfilter',
      'material': 'Material',
      'resolution': 'Auflösung / DPI',
      'cmyk': 'Farbprofil CMYK',
      'safe-area': 'Sicherheitsbereich',
      'copypaste': 'Kopieren & Einfügen',
      'multiselect': 'Mehrfachauswahl',
      'layer-order': 'Ebenen-Reihenfolge',
      'birthday': 'Geburtstagsbanner',
      'wedding': 'Hochzeitsbanner',
      'business': 'Werbebanner',
      'bauzaun': 'Bauzaunbanner',
      'rollup': 'Roll-Up Display',
      'poster': 'Poster / Plakat',
      'snap-off': 'Einrasten deaktivieren',
      'assistant': 'Hilfe-Assistent',
      'onboarding': 'Einführung / Tour',
      'quantity': 'Bestellmenge',
      'fileformat': 'Dateiformate',
      'curved-text': 'Gebogener Text',
      'collage-layouts': 'Collage-Layouts',
      'outdoor': 'Außeneinsatz',
      'indoor': 'Inneneinsatz',
    };
    return labels[id] || id;
  }

  _fallback() {
    return {
      answer:
        'Entschuldigung, dazu habe ich leider keine passende Antwort gefunden. Versuche es mit anderen Stichwörtern wie "Text", "Formen", "Beschnitt", "Verarbeitung" oder "Tastenkürzel". Oder frag mich "Was kann ich hier machen?" für eine Übersicht!',
      actions: [],
      confidence: 0,
      id: null,
      related: ['overview', 'shortcuts', 'finishing'],
    };
  }
}

// Singleton export
const assistant = new DesignerAssistant();
export default assistant;
